import {
  Bookmark,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  GitCompareArrows,
  Trash2,
} from "lucide-react";
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api } from "../../api/client.js";
import { type DraftedFixResult, type ModelAssistInput, modelApi } from "../../api/model.js";
import type {
  ComparisonResult,
  FindingFacets,
  FindingAssigneeOption,
  FindingQueryState,
  FindingSortKey,
  FindingView,
  HumanValidationInput,
  RemediationProposal,
  SavedFindingView,
  ScanView,
} from "../../api/types.js";
import {
  coverageGapsHref,
  handleInternalNavigation,
  pathWithScanScope,
} from "../../app/routing.js";
import { EmptyState, InlineError, LoadingTable } from "../../components/AsyncState.js";
import { useApiResource } from "../../hooks/useApiResource.js";
import { useDebouncedValue } from "../../hooks/useDebouncedValue.js";
import { downloadExport } from "../../lib/download.js";
import { formatInteger } from "../../lib/format.js";
import { FindingFilters } from "./FindingFilters.js";
import { FindingInspector } from "./FindingInspector.js";
import {
  COLUMN_DEFINITIONS,
  type ColumnKey,
  DEFAULT_VISIBLE_COLUMNS,
  defaultAssignmentDueAt,
  FindingsTable,
  type ModelAssistState,
} from "./FindingsTable.js";
import {
  findingIdFromLocationHash,
  findingIdFromSourceHash,
  findingsHref,
} from "./findingStoryHash.js";
import {
  isEditableKeyboardTarget,
  resolveFindingListShortcut,
  resolveFindingQueueKey,
} from "./findingStoryModel.js";
import {
  ACTIONABLE_STATUSES,
  DEFAULT_FINDING_QUERY,
  filtersForExport,
  findingQueryFromSearch,
  findingQueryToSearch,
  statusesForPreset,
} from "./model.js";

const EMPTY_SCANS: readonly ScanView[] = [];
const PENDING_DECISION_STATUSES = new Set(["candidate", "needs_human"]);

export function pendingDecisionCountFromFacets(
  statuses: readonly { value: string; count: number }[] | undefined,
): number {
  return (statuses ?? [])
    .filter((item) => PENDING_DECISION_STATUSES.has(item.value))
    .reduce((sum, item) => sum + item.count, 0);
}

export function findingsDecisionHeroCopy(input: {
  matchingTotal: number;
  pendingDecisionCount: number | null;
}): string {
  if (input.matchingTotal === 0) return "No findings match this review scope.";
  if (input.pendingDecisionCount === null) {
    return `${formatInteger(input.matchingTotal)} matching finding${input.matchingTotal === 1 ? "" : "s"} in this review scope.`;
  }
  if (input.pendingDecisionCount <= 0) {
    return input.matchingTotal === 1
      ? "This matching finding already has a recorded decision."
      : `All ${formatInteger(input.matchingTotal)} matching findings already have a recorded decision.`;
  }
  if (input.pendingDecisionCount === 1) return "1 finding needs a clear decision.";
  return `${formatInteger(input.pendingDecisionCount)} findings need a clear decision.`;
}

function countFacetValues<T extends string | number>(
  facets: readonly { value: T; count: number }[] | undefined,
  values: readonly T[],
): number {
  return (facets ?? [])
    .filter((item) => values.includes(item.value))
    .reduce((sum, item) => sum + item.count, 0);
}

export interface FindingThreatSummary {
  readonly total: number;
  readonly criticalHigh: number;
  readonly pendingDecision: number | null;
  readonly confirmed: number;
  readonly replayMatched: number;
  readonly independentAiReview: number;
}

export function findingThreatSummary(
  facets: FindingFacets | null,
  total: number,
  pendingDecisionCount: number | null,
): FindingThreatSummary {
  return {
    total,
    criticalHigh: countFacetValues(facets?.severities, ["critical", "high"]),
    pendingDecision: pendingDecisionCount,
    confirmed: countFacetValues(facets?.statuses, ["confirmed"]),
    replayMatched: countFacetValues(facets?.replayStatuses, ["matched"]),
    independentAiReview: countFacetValues(facets?.engines, ["codex-security"]),
  };
}

function queryPatchActive(state: FindingQueryState, patch: object): boolean {
  return Object.entries(patch).every(([key, value]) => {
    if (key === "page") return true;
    return JSON.stringify(state[key as keyof FindingQueryState]) === JSON.stringify(value);
  });
}

function FindingResultTiles({
  total,
  summary,
  facets,
  state,
  onApply,
}: {
  total: number;
  summary: FindingThreatSummary;
  facets: FindingFacets | null;
  state: FindingQueryState;
  onApply: (patch: Partial<FindingQueryState>) => void;
}) {
  const tiles: Array<{
    id: string;
    label: string;
    count: number | null;
    hint: string;
    patch: Partial<FindingQueryState> | null;
  }> = [
    {
      id: "matching",
      label: "Matching",
      count: total,
      hint: "Rows in the current filter",
      patch: null,
    },
    {
      id: "critical",
      label: "Critical",
      count: countFacetValues(facets?.severities, ["critical"]),
      hint: "Highest severity in this result set",
      patch: { severities: ["critical"], page: 1 },
    },
    {
      id: "high",
      label: "High",
      count: countFacetValues(facets?.severities, ["high"]),
      hint: "High severity in this result set",
      patch: { severities: ["high"], page: 1 },
    },
    {
      id: "needs-human",
      label: "Needs human",
      count: summary.pendingDecision,
      hint: "Still waiting on a reviewer decision",
      patch: { statuses: ["needs_human"], preset: "custom", page: 1 },
    },
    {
      id: "confirmed",
      label: "Confirmed",
      count: summary.confirmed,
      hint: "Replay-backed confirmed findings",
      patch: { statuses: ["confirmed"], preset: "custom", page: 1 },
    },
    {
      id: "replay",
      label: "Replay matched",
      count: summary.replayMatched,
      hint: "Evidence replayed against the frozen snapshot",
      patch: { replayStatuses: ["matched"], page: 1 },
    },
    {
      id: "mismatch",
      label: "Replay issues",
      count: countFacetValues(facets?.replayStatuses, ["mismatch"]),
      hint: "Cited bytes no longer match the snapshot",
      patch: { replayStatuses: ["mismatch"], page: 1 },
    },
    {
      id: "ai-review",
      label: "Evidence brief",
      count: summary.independentAiReview,
      hint: "Independent AI-reviewed candidates",
      patch: { engineIds: ["codex-security"], page: 1 },
    },
  ];

  return (
    <section className="finding-result-tiles" aria-label="Filtered result breakdown">
      {tiles.map((tile) => {
        const value = tile.count === null ? "Unavailable" : formatInteger(tile.count);
        const active = tile.patch ? queryPatchActive(state, tile.patch) : false;
        const content = (
          <>
            <span className="finding-result-tiles__label">{tile.label}</span>
            <strong>{value}</strong>
          </>
        );
        if (!tile.patch) {
          return (
            <div className="finding-result-tiles__card" key={tile.id} title={tile.hint}>
              {content}
            </div>
          );
        }
        return (
          <button
            className={`finding-result-tiles__card finding-result-tiles__card--button${active ? " finding-result-tiles__card--active" : ""}`}
            type="button"
            key={tile.id}
            title={tile.hint}
            aria-pressed={active}
            aria-label={`${tile.label}: ${value}. ${tile.hint}`}
            onClick={() => onApply(tile.patch ?? {})}
          >
            {content}
          </button>
        );
      })}
    </section>
  );
}

function BulkAssignmentControl({
  selected,
  assignees,
  pending,
  message,
  onApply,
}: {
  selected: readonly FindingView[];
  assignees: readonly FindingAssigneeOption[];
  pending: boolean;
  message: string;
  onApply: (input: {
    assignment: null | { assigneeId: string; dueAt: string };
    rationale: string;
  }) => Promise<void>;
}) {
  const [mode, setMode] = useState<"assign" | "clear">("assign");
  const [assigneeId, setAssigneeId] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [rationale, setRationale] = useState("");
  const valid =
    selected.length > 0 &&
    selected.length <= 100 &&
    rationale.trim().length >= 10 &&
    (mode === "clear" || (assigneeId.length > 0 && dueAt.length > 0));
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    await onApply({
      assignment: mode === "clear" ? null : { assigneeId, dueAt: new Date(dueAt).toISOString() },
      rationale: rationale.trim(),
    });
  };
  return (
    <details className="bulk-assignment">
      <summary className="control-button">Assign / SLA ({selected.length})</summary>
      <form className="bulk-assignment__form" onSubmit={(event) => void submit(event)}>
        <p>
          Applies atomically to the selected findings. A stale finding aborts the whole change;
          canonical truth and severity are unchanged.
        </p>
        <label>
          Action
          <select value={mode} onChange={(event) => setMode(event.target.value as typeof mode)}>
            <option value="assign">Set owner and SLA</option>
            <option value="clear">Clear assignment</option>
          </select>
        </label>
        {mode === "assign" ? (
          <>
            <label>
              Assignee
              <select value={assigneeId} onChange={(event) => setAssigneeId(event.target.value)}>
                <option value="">Select tenant member…</option>
                {assignees.map((assignee) => (
                  <option value={assignee.principalId} key={assignee.principalId}>
                    {assignee.principalId} · {assignee.role.replace("_", " ")}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Due date
              <input
                type="datetime-local"
                value={dueAt}
                min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
                onChange={(event) => setDueAt(event.target.value)}
              />
            </label>
          </>
        ) : null}
        <label>
          Rationale
          <textarea
            value={rationale}
            minLength={10}
            maxLength={4000}
            required
            onChange={(event) => setRationale(event.target.value)}
          />
        </label>
        <button className="button button--small" type="submit" disabled={pending || !valid}>
          {pending ? "Applying…" : mode === "clear" ? "Clear assignments" : "Apply assignment"}
        </button>
        <span role="status" aria-live="polite">
          {message}
        </span>
      </form>
    </details>
  );
}

export function pruneExpandedToVisible(
  expanded: ReadonlySet<string>,
  visibleIds: readonly string[],
): Set<string> {
  if (expanded.size === 0) return expanded as Set<string>;
  const visible = new Set(visibleIds);
  const next = new Set([...expanded].filter((id) => visible.has(id)));
  return next.size === expanded.size ? (expanded as Set<string>) : next;
}

export function findingsQueryForSelectedScan(
  current: FindingQueryState,
  selectedScanId: string | undefined,
): FindingQueryState {
  return current.scanId === selectedScanId
    ? current
    : { ...current, scanId: selectedScanId, page: 1 };
}

/** Null means skip rewrite so a stale scanId cannot land back on all-scans. */
export function findingsLocationForSelectedScan(input: {
  query: FindingQueryState;
  selectedScanId: string | undefined;
  currentSearch: string;
}): string | null {
  if (input.query.scanId !== input.selectedScanId) return null;
  const params = new URLSearchParams(
    input.currentSearch.startsWith("?") ? input.currentSearch.slice(1) : input.currentSearch,
  );
  const allScans = !input.selectedScanId && params.get("scanScope") === "all";
  return pathWithScanScope(
    `/findings${findingQueryToSearch(input.query)}`,
    input.selectedScanId,
    allScans,
  );
}

export { findingIdFromSourceHash };

function SavedViewsMenu({
  views,
  loading,
  pending,
  message,
  onSave,
  onApply,
  onDelete,
}: {
  views: readonly SavedFindingView[];
  loading: boolean;
  pending: boolean;
  message: string;
  onSave: (name: string) => Promise<boolean>;
  onApply: (view: SavedFindingView) => void;
  onDelete: (view: SavedFindingView) => Promise<void>;
}) {
  const [name, setName] = useState("");

  const save = async (event: FormEvent) => {
    event.preventDefault();
    const normalized = name.trim();
    if (!normalized) return;
    if (await onSave(normalized)) setName("");
  };

  return (
    <details className="menu-popover saved-views">
      <summary className="control-button">
        <Bookmark aria-hidden="true" size={15} />
        Saved views
        {views.length > 0 ? <span className="count-chip">{views.length}</span> : null}
        <ChevronDown aria-hidden="true" size={14} />
      </summary>
      <div className="menu-popover__panel saved-views__panel">
        <form onSubmit={(event) => void save(event)}>
          <label htmlFor="saved-view-name">Save the current filters</label>
          <div className="saved-views__create">
            <input
              id="saved-view-name"
              value={name}
              maxLength={80}
              placeholder="View name"
              onChange={(event) => setName(event.target.value)}
            />
            <button
              className="button button--small"
              type="submit"
              disabled={pending || !name.trim()}
            >
              Save
            </button>
          </div>
        </form>
        <fieldset className="saved-views__list">
          <legend className="sr-only">Saved findings views</legend>
          {loading ? <span className="filter-menu__empty">Loading saved views…</span> : null}
          {!loading && views.length === 0 ? (
            <span className="filter-menu__empty">No saved views yet.</span>
          ) : null}
          {views.map((view) => (
            <div className="saved-views__item" key={view.id}>
              <button className="text-button" type="button" onClick={() => onApply(view)}>
                {view.name}
              </button>
              <button
                className="icon-button icon-button--small"
                type="button"
                aria-label={`Delete saved view ${view.name}`}
                title={`Delete ${view.name}`}
                disabled={pending}
                onClick={() => void onDelete(view)}
              >
                <Trash2 aria-hidden="true" size={14} />
              </button>
            </div>
          ))}
        </fieldset>
        <span className="saved-views__message" aria-live="polite">
          {message}
        </span>
      </div>
    </details>
  );
}

export function defaultScanComparison(
  scans: readonly ScanView[],
  selectedScanId?: string,
): { baseScanId: string; headScanId: string } {
  if (!selectedScanId) return { baseScanId: "", headScanId: "" };
  const selected = scans.find((scan) => scan.id === selectedScanId);
  if (!selected || !["completed", "completed_with_errors"].includes(selected.status)) {
    return { baseScanId: "", headScanId: "" };
  }
  const previous = scans
    .filter(
      (scan) =>
        scan.id !== selected.id &&
        scan.repositoryId === selected.repositoryId &&
        ["completed", "completed_with_errors"].includes(scan.status) &&
        new Date(scan.startedAt).getTime() < new Date(selected.startedAt).getTime(),
    )
    .sort((left, right) => right.startedAt.localeCompare(left.startedAt))[0];
  return { baseScanId: previous?.id ?? "", headScanId: selected.id };
}

export function ScanComparison({
  scans,
  selectedScanId,
}: {
  scans: readonly ScanView[];
  selectedScanId?: string | undefined;
}) {
  const defaults = defaultScanComparison(scans, selectedScanId);
  const [open, setOpen] = useState(false);
  const [baseScanId, setBaseScanId] = useState(defaults.baseScanId);
  const [headScanId, setHeadScanId] = useState(defaults.headScanId);
  const [result, setResult] = useState<ComparisonResult | null>(null);
  const [message, setMessage] = useState("");
  const [pending, setPending] = useState(false);
  const comparableScans = scans
    .filter((scan) => ["completed", "completed_with_errors"].includes(scan.status))
    .sort((left, right) => Date.parse(left.startedAt) - Date.parse(right.startedAt));
  const base = comparableScans.find((scan) => scan.id === baseScanId);
  const eligibleHeads = base
    ? comparableScans.filter(
        (scan) =>
          scan.repositoryId === base.repositoryId &&
          Date.parse(scan.startedAt) > Date.parse(base.startedAt),
      )
    : comparableScans;

  useEffect(() => {
    const next = defaultScanComparison(scans, selectedScanId);
    setBaseScanId(next.baseScanId);
    setHeadScanId(next.headScanId);
    setResult(null);
    setMessage("");
  }, [scans, selectedScanId]);

  const compare = async (event: FormEvent) => {
    event.preventDefault();
    if (!baseScanId || !headScanId || baseScanId === headScanId) return;
    setPending(true);
    setMessage("");
    try {
      setResult(await api.getComparison(baseScanId, headScanId));
    } catch (error) {
      setResult(null);
      setMessage(error instanceof ApiError ? error.message : "The scans could not be compared.");
    } finally {
      setPending(false);
    }
  };
  const counts = result
    ? Object.fromEntries(
        ["new", "persistent", "fixed", "reopened"].map((outcome) => [
          outcome,
          result.rows.filter((row) => row.outcome === outcome).length,
        ]),
      )
    : null;

  return (
    <div className="scan-comparison">
      <button
        className="scan-comparison__toggle"
        type="button"
        aria-expanded={open}
        aria-controls="scan-comparison-panel"
        onClick={() => setOpen((current) => !current)}
      >
        <GitCompareArrows aria-hidden="true" size={17} />
        Compare scans
      </button>
      {open ? (
        <div className="scan-comparison__panel" id="scan-comparison-panel">
          <p>
            See what is new, persistent, fixed, or reopened across two scans of the same repository.
            The comparison is read-only and never changes either scan.
          </p>
          <form className="scan-comparison__form" onSubmit={(event) => void compare(event)}>
            <label>
              <span>Earlier scan</span>
              <select
                value={baseScanId}
                onChange={(event) => {
                  const nextBaseId = event.target.value;
                  const nextBase = comparableScans.find((scan) => scan.id === nextBaseId);
                  const nextHead = nextBase
                    ? comparableScans.find(
                        (scan) =>
                          scan.repositoryId === nextBase.repositoryId &&
                          Date.parse(scan.startedAt) > Date.parse(nextBase.startedAt),
                      )
                    : undefined;
                  setBaseScanId(nextBaseId);
                  setHeadScanId(nextHead?.id ?? "");
                  setResult(null);
                }}
              >
                <option value="">Select earlier scan…</option>
                {comparableScans.map((scan) => (
                  <option key={scan.id} value={scan.id}>
                    {scan.repositoryName} · {new Date(scan.startedAt).toLocaleString()}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Later scan</span>
              <select value={headScanId} onChange={(event) => setHeadScanId(event.target.value)}>
                <option value="">Select later scan…</option>
                {eligibleHeads
                  .filter((scan) => scan.id !== baseScanId)
                  .map((scan) => (
                    <option key={scan.id} value={scan.id}>
                      {scan.repositoryName} · {new Date(scan.startedAt).toLocaleString()}
                    </option>
                  ))}
              </select>
            </label>
            <button
              className="button button--primary"
              type="submit"
              disabled={!baseScanId || !headScanId || pending}
            >
              {pending ? "Comparing…" : "Compare scans"}
            </button>
          </form>
          {message ? <p className="inline-alert inline-alert--error">{message}</p> : null}
          {result && !result.comparable ? (
            <p className="inline-alert inline-alert--error">{result.reason}</p>
          ) : null}
          {result?.comparable && counts ? (
            <>
              {result.orderNormalized ? (
                <p className="inline-alert inline-alert--info" role="status">
                  The selections were reordered chronologically so “fixed” always means present
                  before and absent afterward.
                </p>
              ) : null}
              <dl className="scan-comparison__results" aria-label="Scan comparison results">
                {Object.entries(counts).map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{formatInteger(value)}</dd>
                  </div>
                ))}
              </dl>
              <div className="scan-comparison__rows">
                <strong>Finding identity changes</strong>
                <p>
                  Rule and source location form the stable identity. Showing changed findings first,
                  followed by persistent findings, up to 50 rows.
                </p>
                <div className="scan-comparison__table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Outcome</th>
                        <th>Rule and source location</th>
                        <th>Why</th>
                        <th>Evidence and fix</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...result.rows]
                        .sort((left, right) => {
                          const persistent = (value: string) => (value === "persistent" ? 1 : 0);
                          return (
                            persistent(left.outcome) - persistent(right.outcome) ||
                            left.stableKey.localeCompare(right.stableKey)
                          );
                        })
                        .slice(0, 50)
                        .map((row) => (
                          <tr key={`${row.outcome}-${row.stableKey}`}>
                            <td>
                              <span
                                className={`comparison-outcome comparison-outcome--${row.outcome}`}
                              >
                                {row.outcome.replace("_", " ")}
                              </span>
                            </td>
                            <td>
                              <code>{row.stableKey}</code>
                            </td>
                            <td>{row.reason ?? "—"}</td>
                            <td>
                              <div className="comparison-actions">
                                {row.baseFindingId ? (
                                  <a
                                    href={`/findings?scanId=${encodeURIComponent(result.baseScanId)}#source-${encodeURIComponent(row.baseFindingId)}-L${row.lineStart ?? 1}`}
                                  >
                                    Open earlier evidence
                                  </a>
                                ) : null}
                                {row.headFindingId ? (
                                  <a
                                    href={`/findings?scanId=${encodeURIComponent(result.headScanId)}#source-${encodeURIComponent(row.headFindingId)}-L${row.lineStart ?? 1}`}
                                  >
                                    Open later evidence
                                  </a>
                                ) : null}
                                {row.outcome === "fixed" && row.baseFindingId ? (
                                  <a
                                    href={`/remediation?findingId=${encodeURIComponent(row.baseFindingId)}`}
                                  >
                                    View generated fix
                                  </a>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

const MODEL_PROVIDER_PRIORITY: Readonly<Record<string, number | undefined>> = Object.freeze({
  openai: 0,
  "openai-oauth": 1,
  anthropic: 2,
  "anthropic-oauth": 3,
  deepseek: 4,
});

function orderModelProviders<T extends { readonly id: string }>(providers: readonly T[]): T[] {
  return [...providers].sort((left, right) => {
    const leftPriority = MODEL_PROVIDER_PRIORITY[left.id] ?? 100;
    const rightPriority = MODEL_PROVIDER_PRIORITY[right.id] ?? 100;
    return leftPriority - rightPriority || left.id.localeCompare(right.id);
  });
}

export function findingPreferenceKey(
  tenantId: string,
  principalId: string,
  preference: "columns" | "facet-rail-open",
): string {
  return `assure:${encodeURIComponent(tenantId)}:${encodeURIComponent(principalId)}:findings:${preference}`;
}

function initialColumns(tenantId: string, principalId: string): Set<ColumnKey> {
  try {
    const stored = JSON.parse(
      window.localStorage.getItem(findingPreferenceKey(tenantId, principalId, "columns")) ?? "[]",
    ) as unknown;
    if (Array.isArray(stored)) {
      const allowed = new Set<ColumnKey>(COLUMN_DEFINITIONS.map((column) => column.key));
      const parsed = stored.filter(
        (item): item is ColumnKey => typeof item === "string" && allowed.has(item as ColumnKey),
      );
      if (parsed.length > 0) return new Set(parsed);
    }
  } catch {
    // Defaults remain available when stored preferences are invalid.
  }
  return new Set(DEFAULT_VISIBLE_COLUMNS);
}

function initialFacetRailOpen(tenantId: string, principalId: string): boolean {
  try {
    return (
      window.localStorage.getItem(
        findingPreferenceKey(tenantId, principalId, "facet-rail-open"),
      ) === "true"
    );
  } catch {
    return false;
  }
}

export function FindingsPage({
  initialSearch = "",
  principalId = "unknown",
  tenantId = "unknown",
  selectedScanId,
  onScanChange,
  canRemediate = false,
  canReview = false,
  navigate,
}: {
  initialSearch?: string | undefined;
  principalId?: string | undefined;
  tenantId?: string | undefined;
  selectedScanId?: string | undefined;
  onScanChange?: ((scanId: string | undefined) => void) | undefined;
  canRemediate?: boolean | undefined;
  canReview?: boolean | undefined;
  navigate?: ((path: string) => void) | undefined;
} = {}) {
  const [query, setQuery] = useState<FindingQueryState>(() =>
    findingQueryFromSearch(initialSearch),
  );
  const [advancedOpen, setAdvancedOpenState] = useState(() =>
    initialFacetRailOpen(tenantId, principalId),
  );
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(() =>
    initialColumns(tenantId, principalId),
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [locationHash, setLocationHash] = useState(() => window.location.hash);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<Error | null>(null);
  const [shareMessage, setShareMessage] = useState("");
  const [savedViewPending, setSavedViewPending] = useState(false);
  const [savedViewMessage, setSavedViewMessage] = useState("");
  const [assignmentAssignees, setAssignmentAssignees] = useState<readonly FindingAssigneeOption[]>(
    [],
  );
  const [assignmentPending, setAssignmentPending] = useState(false);
  const [assignmentPendingId, setAssignmentPendingId] = useState<string | null>(null);
  const [assignmentMessage, setAssignmentMessage] = useState("");
  const [modelProviders, setModelProviders] = useState<
    readonly { id: string; egress: "local" | "remote" }[]
  >([]);
  const [modelExplanations, setModelExplanations] = useState<ReadonlyMap<string, unknown>>(
    new Map(),
  );
  const [modelPending, setModelPending] = useState<ReadonlySet<string>>(new Set());
  const [modelMessages, setModelMessages] = useState<ReadonlyMap<string, string>>(new Map());
  /**
   * Drafted fixes are held per finding, like explanations, but typed: the result
   * is an envelope this API builds rather than a payload a provider chose.
   * `ModelDraftedFixPanel` re-checks it regardless, and specifically re-checks
   * that a patch is only ever shown for a verified outcome.
   */
  const [modelDraftedFixes, setModelDraftedFixes] = useState<ReadonlyMap<string, DraftedFixResult>>(
    new Map(),
  );
  /** Keyed by ARTIFACT id, not finding id: the decision is about the artifact. */
  const [modelDecisions, setModelDecisions] = useState<
    ReadonlyMap<string, "accepted" | "rejected">
  >(new Map());
  const [modelDecisionPending, setModelDecisionPending] = useState<ReadonlySet<string>>(new Set());
  const [autoRemediations, setAutoRemediations] = useState<
    ReadonlyMap<string, RemediationProposal>
  >(new Map());
  const [autoRemediationPending, setAutoRemediationPending] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const [autoRemediationMessages, setAutoRemediationMessages] = useState<
    ReadonlyMap<string, string>
  >(new Map());
  const debouncedQuery = useDebouncedValue(query, 300);
  const storyId = findingIdFromLocationHash(locationHash);

  useEffect(() => {
    const onHash = () => setLocationHash(window.location.hash);
    window.addEventListener("hashchange", onHash);
    window.addEventListener("popstate", onHash);
    return () => {
      window.removeEventListener("hashchange", onHash);
      window.removeEventListener("popstate", onHash);
    };
  }, []);

  useEffect(() => {
    setQuery(findingQueryFromSearch(initialSearch));
  }, [initialSearch]);

  useEffect(() => {
    setQuery((current) => findingsQueryForSelectedScan(current, selectedScanId));
  }, [selectedScanId]);

  useEffect(() => {
    if (query.scanId === selectedScanId) return;
    onScanChange?.(query.scanId);
  }, [onScanChange, query.scanId, selectedScanId]);

  useEffect(() => {
    setAdvancedOpenState(initialFacetRailOpen(tenantId, principalId));
    setVisibleColumns(initialColumns(tenantId, principalId));
  }, [principalId, tenantId]);

  const setAdvancedOpen = useCallback(
    (open: boolean) => {
      setAdvancedOpenState(open);
      try {
        window.localStorage.setItem(
          findingPreferenceKey(tenantId, principalId, "facet-rail-open"),
          String(open),
        );
      } catch {
        // In-memory facet-rail state still works without storage.
      }
    },
    [principalId, tenantId],
  );

  useEffect(() => {
    const target = findingsLocationForSelectedScan({
      query,
      selectedScanId,
      currentSearch: window.location.search,
    });
    if (!target) return;
    if (window.location.pathname + window.location.search !== target) {
      window.history.replaceState({}, "", `${target}${window.location.hash}`);
    }
  }, [query, selectedScanId]);

  /**
   * Which providers this tenant has actually configured, or none.
   *
   * Failure is silent and means the same thing as an empty list: no model
   * assistance control renders. The two ways it fails are both ordinary states
   * rather than faults -- a session that is not this tenant's owner gets a 403,
   * because the provider listing discloses which third parties this tenant ships
   * code to, and a deployment with no model configured simply has nothing
   * active. Neither should put an error banner on the findings page.
   */
  useEffect(() => {
    const controller = new AbortController();
    modelApi
      .listProviders(controller.signal)
      .then((listing) => {
        setModelProviders(
          orderModelProviders(
            listing.items
              .filter((item) => item.status === "active")
              .map((item) => ({ id: item.id, egress: item.egress })),
          ),
        );
      })
      .catch(() => {
        setModelProviders([]);
      });
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (!canReview) {
      setAssignmentAssignees([]);
      return;
    }
    const controller = new AbortController();
    api
      .listFindingAssignees(controller.signal)
      .then((response) => setAssignmentAssignees(response.items))
      .catch(() => setAssignmentAssignees([]));
    return () => controller.abort();
  }, [canReview]);

  const loadFindings = useCallback(
    (signal: AbortSignal) => api.getFindings(debouncedQuery, signal),
    [debouncedQuery],
  );
  const loadFacets = useCallback(
    (signal: AbortSignal) => api.getFindingFacets(debouncedQuery, signal),
    [debouncedQuery],
  );
  const loadScans = useCallback(
    (signal: AbortSignal) => api.getScans({ page: 1, pageSize: 500 }, signal),
    [],
  );
  const loadSavedViews = useCallback(
    (signal: AbortSignal) => api.listSavedFindingViews(signal),
    [],
  );
  const findings = useApiResource(loadFindings);
  const facets = useApiResource(loadFacets);
  const scans = useApiResource(loadScans);
  const savedViews = useApiResource(loadSavedViews);
  const items = findings.data?.items ?? [];
  const total = findings.data?.total ?? 0;
  const itemIds = items.map((item) => item.id);
  const listedStory = items.find((item) => item.id === storyId) ?? null;
  const loadFetched = useCallback(
    (signal: AbortSignal) =>
      storyId && !listedStory ? api.getFinding(storyId, signal) : Promise.resolve(null),
    [listedStory, storyId],
  );
  const fetched = useApiResource(loadFetched);
  const storyFinding = listedStory ?? fetched.data;
  useEffect(() => {
    if (items.length === 0) {
      setFocusedId(null);
      return;
    }
    setFocusedId((current) =>
      current && items.some((item) => item.id === current) ? current : (items[0]?.id ?? null),
    );
  }, [items]);
  const pendingDecisionCount = facets.data
    ? pendingDecisionCountFromFacets(facets.data.statuses)
    : null;
  const threatSummary = findingThreatSummary(facets.data, total, pendingDecisionCount);

  useEffect(() => {
    setExpanded((current) =>
      pruneExpandedToVisible(
        current,
        items.map((item) => item.id),
      ),
    );
  }, [items]);

  useEffect(() => {
    const findingId = findingIdFromSourceHash(window.location.hash);
    if (!findingId || !items.some((finding) => finding.id === findingId)) return;
    setExpanded((current) => new Set(current).add(findingId));
    window.requestAnimationFrame(() => {
      document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ block: "center" });
    });
  }, [items]);

  const visibleStart = total === 0 ? 0 : (query.page - 1) * query.pageSize + 1;
  const visibleEnd = Math.min(query.page * query.pageSize, total);
  const totalPages = Math.max(1, Math.ceil(total / query.pageSize));

  const goToFindingsHref = useCallback(
    (href: string, hash: string) => {
      const target = new URL(href, window.location.origin);
      const hashOnly =
        target.pathname === window.location.pathname &&
        target.search === window.location.search;
      // Opening the story only changes the hash. Keep that transition inside
      // Findings so the app shell and list resources are not rerendered/refetched.
      if (hashOnly || !navigate) window.history.pushState({}, "", href);
      else navigate(href);
      setLocationHash(hash);
    },
    [navigate],
  );

  const automaticReviewIds = useRef(new Set<string>());
  const loadAutomaticReview = useCallback((id: string) => {
    if (automaticReviewIds.current.has(id)) return;
    automaticReviewIds.current.add(id);
    void modelApi
      .getAutomaticExplanation(id)
      .then((result) => {
        if (result.outcome !== "explained") return;
        setModelExplanations((current) => new Map(current).set(id, result.explanation));
        setModelMessages((current) =>
          new Map(current).set(
            id,
            "The evidence-bound assistant generated this advisory brief automatically after the scan.",
          ),
        );
      })
      .catch(() => undefined);
  }, []);

  const findingsSearch = useCallback(() => {
    const location = findingsLocationForSelectedScan({
      query,
      selectedScanId,
      currentSearch: window.location.search,
    });
    return location?.startsWith("/findings")
      ? location.slice("/findings".length)
      : findingQueryToSearch(query);
  }, [query, selectedScanId]);

  const openStory = useCallback(
    (id: string) => {
      goToFindingsHref(findingsHref(findingsSearch(), id), `#${id}`);
      setFocusedId(id);
    },
    [findingsSearch, goToFindingsHref],
  );

  useEffect(() => {
    if (!storyId) return;
    // The advisory brief is optional. Let the selected finding paint first,
    // then load it without competing with the navigation render.
    const timer = window.setTimeout(() => loadAutomaticReview(storyId), 250);
    return () => window.clearTimeout(timer);
  }, [loadAutomaticReview, storyId]);

  const closeStory = useCallback(() => {
    goToFindingsHref(findingsHref(findingsSearch(), null), "");
  }, [findingsSearch, goToFindingsHref]);

  const toggleColumn = (column: ColumnKey) => {
    setVisibleColumns((current) => {
      const next = new Set(current);
      if (next.has(column)) next.delete(column);
      else next.add(column);
      try {
        window.localStorage.setItem(
          findingPreferenceKey(tenantId, principalId, "columns"),
          JSON.stringify([...next]),
        );
      } catch {
        // In-memory column preferences still work without storage.
      }
      return next;
    });
  };

  const resetColumns = () => {
    const next = new Set<ColumnKey>(DEFAULT_VISIBLE_COLUMNS);
    setVisibleColumns(next);
    try {
      window.localStorage.setItem(
        findingPreferenceKey(tenantId, principalId, "columns"),
        JSON.stringify([...next]),
      );
    } catch {
      // Ignore storage failures.
    }
  };

  const focusCriticalHighPaths = () => {
    setSelected(new Set());
    setExpanded(new Set());
    setQuery((current) => ({
      ...current,
      preset: "actionable",
      statuses: statusesForPreset("actionable"),
      severities: ["critical", "high"],
      page: 1,
    }));
  };

  const focusAiReviewedEvidence = () => {
    setSelected(new Set());
    setExpanded(new Set());
    setQuery((current) => ({
      ...current,
      preset: "actionable",
      statuses: statusesForPreset("actionable"),
      replayStatuses: ["matched"],
      page: 1,
    }));
  };

  const focusReplayMatchedProof = () => {
    setSelected(new Set());
    setExpanded(new Set());
    setQuery((current) => ({
      ...current,
      replayStatuses: ["matched"],
      page: 1,
    }));
  };

  const toggleSelected = useCallback(
    (id: string) => {
      if (!selected.has(id) && selected.size >= 500) {
        setExportError(new Error("Selected CSV export is limited to 500 finding IDs."));
        return;
      }
      setSelected((current) => {
        const next = new Set(current);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [selected],
  );

  const togglePageSelected = () => {
    const unselectedOnPage = items.filter((finding) => !selected.has(finding.id));
    const allSelected = items.length > 0 && unselectedOnPage.length === 0;
    if (!allSelected && selected.size + unselectedOnPage.length > 500) {
      setExportError(new Error("Selecting this page would exceed the 500-ID export limit."));
      return;
    }
    setSelected((current) => {
      const next = new Set(current);
      for (const finding of items) {
        if (allSelected) next.delete(finding.id);
        else next.add(finding.id);
      }
      return next;
    });
  };

  const sort = (sortKey: FindingSortKey) => {
    setQuery((current) => ({
      ...current,
      sort: sortKey,
      direction:
        current.sort === sortKey ? (current.direction === "desc" ? "asc" : "desc") : "desc",
      page: 1,
    }));
  };

  const performExport = async (mode: "selected" | "filtered") => {
    setExporting(true);
    setExportError(null);
    try {
      const result =
        mode === "selected"
          ? await api.exportFindings({
              findingIds: [...selected],
              sortBy: query.sort,
              sortDirection: query.direction,
              page: 1,
              pageSize: 500,
            })
          : await api.exportFindings({
              ...filtersForExport(query),
              page: 1,
              pageSize: 500,
            });
      downloadExport(result);
    } catch (reason) {
      setExportError(
        reason instanceof Error ? reason : new Error("The export could not be prepared."),
      );
    } finally {
      setExporting(false);
    }
  };

  /**
   * Ask a configured model to explain one finding.
   *
   * Every failure lands in the per-finding message rather than in the page's
   * error state. A provider being down, a finding with no verdict yet, and a
   * deployment with no model configured are all ordinary answers, and none of
   * them is a reason to stop rendering the findings table.
   *
   * The payload is stored exactly as it arrived. Nothing here parses it, reads a
   * field of it, or decides whether it is renderable -- `ModelExplanationPanel`
   * does all three, and its marker is a constant it emits without consulting the
   * payload, so a reply cannot arrive here in a shape that turns it off.
   */
  const explain = useCallback(async (findingId: string, input: ModelAssistInput) => {
    setModelPending((current) => new Set(current).add(findingId));
    setModelMessages((current) => new Map(current).set(findingId, "Asking the model."));
    try {
      const result = await modelApi.explainFinding(findingId, input);
      setModelExplanations((current) => {
        const next = new Map(current);
        if (result.outcome === "explained") next.set(findingId, result.explanation);
        else next.delete(findingId);
        return next;
      });
      setModelMessages((current) =>
        new Map(current).set(
          findingId,
          result.outcome === "explained"
            ? "The model answered. Its prose is below, marked as model-generated."
            : (result.reason ?? "The model produced no explanation."),
        ),
      );
    } catch (reason) {
      setModelMessages((current) =>
        new Map(current).set(
          findingId,
          reason instanceof ApiError && reason.status === 403
            ? "Model assistance is limited to this tenant's owner."
            : "The explanation request could not be completed.",
        ),
      );
    } finally {
      setModelPending((current) => {
        const next = new Set(current);
        next.delete(findingId);
        return next;
      });
    }
  }, []);

  /**
   * Ask for a drafted fix, and keep whatever comes back verbatim.
   *
   * Unlike `explain`, every outcome is stored, not just the successful one. A
   * refusal is the most likely answer for most findings -- only credential
   * classes have a signal independent of the rule that raised them -- and the
   * panel's job is to say so plainly rather than to leave the operator looking
   * at a button that appeared to do nothing.
   *
   * The result is stored exactly as it arrived. In particular this function does
   * not read `patch`, and there is no branch anywhere on this page that reaches
   * for a replacement line the server declined to return.
   */
  const draftFix = useCallback(async (findingId: string, input: ModelAssistInput) => {
    setModelPending((current) => new Set(current).add(findingId));
    setModelMessages((current) => new Map(current).set(findingId, "Asking the model to draft."));
    try {
      const result = await modelApi.draftFix(findingId, input);
      setModelDraftedFixes((current) => new Map(current).set(findingId, result));
      setModelMessages((current) =>
        new Map(current).set(
          findingId,
          result.outcome === "verified"
            ? "A fix was drafted and independently checked. It is below."
            : result.outcome === "rejected"
              ? "A fix was drafted and the check refused it. The line is withheld."
              : (result.reason ?? "No fix was drafted."),
        ),
      );
    } catch (reason) {
      setModelMessages((current) =>
        new Map(current).set(
          findingId,
          reason instanceof ApiError && reason.status === 403
            ? "Model assistance is limited to this tenant's owner."
            : "The drafted-fix request could not be completed.",
        ),
      );
    } finally {
      setModelPending((current) => {
        const next = new Set(current);
        next.delete(findingId);
        return next;
      });
    }
  }, []);

  /**
   * Record an operator's verdict on a drafted fix.
   *
   * Held locally once it succeeds so the controls retire. The server answers 409
   * to a second decision -- an artifact that could be re-decided is not a record
   * of who decided it -- and the point of tracking it here is that the ordinary
   * path never provokes that conflict.
   */
  const decideArtifact = useCallback(
    async (findingId: string, artifactId: string, decision: "accepted" | "rejected") => {
      setModelDecisionPending((current) => new Set(current).add(artifactId));
      try {
        await modelApi.decideArtifact(artifactId, decision);
        setModelDecisions((current) => new Map(current).set(artifactId, decision));
        setModelMessages((current) =>
          new Map(current).set(
            findingId,
            `Recorded as ${decision}. Nothing in your repository changed.`,
          ),
        );
      } catch (reason) {
        setModelMessages((current) =>
          new Map(current).set(
            findingId,
            reason instanceof ApiError && reason.status === 409
              ? "This draft was already decided. A decision cannot be changed."
              : "The decision could not be recorded.",
          ),
        );
      } finally {
        setModelDecisionPending((current) => {
          const next = new Set(current);
          next.delete(artifactId);
          return next;
        });
      }
    },
    [],
  );

  const autoRemediate = useCallback(async (findingId: string) => {
    setAutoRemediationPending((current) => new Set(current).add(findingId));
    setAutoRemediationMessages((current) =>
      new Map(current).set(findingId, "Generating a deterministic patch and verifying its diff."),
    );
    try {
      const proposal = await api.autoGenerateRemediationProposal(findingId);
      setAutoRemediations((current) => new Map(current).set(findingId, proposal));
      setAutoRemediationMessages((current) =>
        new Map(current).set(
          findingId,
          "Patch generated. Review the before/after diff; no repository file was changed.",
        ),
      );
    } catch (reason) {
      setAutoRemediationMessages((current) =>
        new Map(current).set(
          findingId,
          reason instanceof ApiError && reason.status === 403
            ? "Auto-remediation needs an active remediation authorization for this repository. Open governed remediation to authorize view-only patch generation."
            : reason instanceof ApiError && reason.status === 422
              ? "This rule does not yet have a deterministic remediation template. Use the AI draft as an advisory starting point."
              : "The remediation proposal could not be generated.",
        ),
      );
    } finally {
      setAutoRemediationPending((current) => {
        const next = new Set(current);
        next.delete(findingId);
        return next;
      });
    }
  }, []);

  const modelAssist: ModelAssistState = useMemo(
    () => ({
      providers: modelProviders,
      explanations: modelExplanations,
      draftedFixes: modelDraftedFixes,
      decisions: modelDecisions,
      decisionPending: modelDecisionPending,
      pending: modelPending,
      messages: modelMessages,
      remediations: autoRemediations,
      remediationPending: autoRemediationPending,
      remediationMessages: autoRemediationMessages,
      onExplain: (findingId, input) => {
        void explain(findingId, input);
      },
      onDraftFix: (findingId, input) => {
        void draftFix(findingId, input);
      },
      onAutoRemediate: (findingId) => {
        void autoRemediate(findingId);
      },
      onDecideArtifact: (findingId, artifactId, decision) => {
        void decideArtifact(findingId, artifactId, decision);
      },
    }),
    [
      modelProviders,
      modelExplanations,
      modelDraftedFixes,
      modelDecisions,
      modelDecisionPending,
      modelPending,
      modelMessages,
      autoRemediations,
      autoRemediationPending,
      autoRemediationMessages,
      explain,
      draftFix,
      autoRemediate,
      decideArtifact,
    ],
  );

  const validate = async (findingId: string, input: HumanValidationInput) => {
    await api.submitHumanValidation(findingId, input);
    findings.refresh();
    if (storyId && !listedStory) fetched.refresh();
  };

  const setSuppression = async (findingId: string, suppressed: boolean, rationale: string) => {
    await api.setSuppression(findingId, suppressed, rationale);
    findings.refresh();
    if (storyId && !listedStory) fetched.refresh();
  };

  const selectedItems = items.filter((finding) => selected.has(finding.id));
  const applyAssignmentTo = async (
    targets: readonly FindingView[],
    input: {
      assignment: null | { assigneeId: string; dueAt: string };
      rationale: string;
    },
    options: { clearSelection?: boolean; pendingId?: string } = {},
  ) => {
    if (targets.length === 0) return;
    if (targets.length > 100) {
      setAssignmentMessage("At most 100 findings can be assigned atomically.");
      return;
    }
    setAssignmentPending(true);
    setAssignmentPendingId(options.pendingId ?? null);
    setAssignmentMessage("");
    try {
      await api.setFindingAssignments({
        items: targets.map((finding) => ({
          findingId: finding.id,
          expectedFindingUpdatedAt: finding.updatedAt,
          expectedAssignmentRevision: finding.assignment?.revision ?? 0,
        })),
        assignment: input.assignment,
        rationale: input.rationale,
      });
      setAssignmentMessage(
        input.assignment
          ? `Assigned ${targets.length} finding${targets.length === 1 ? "" : "s"}.`
          : `Cleared ${targets.length} assignment${targets.length === 1 ? "" : "s"}.`,
      );
      if (options.clearSelection) setSelected(new Set());
      findings.refresh();
      facets.refresh();
    } catch (error) {
      setAssignmentMessage(
        error instanceof ApiError && error.status === 409
          ? "The selection changed. Refresh and review it before retrying."
          : error instanceof ApiError
            ? error.message
            : "The assignment could not be changed.",
      );
    } finally {
      setAssignmentPending(false);
      setAssignmentPendingId(null);
    }
  };

  const applyAssignment = async (input: {
    assignment: null | { assigneeId: string; dueAt: string };
    rationale: string;
  }) => {
    if (selectedItems.length !== selected.size) {
      setAssignmentMessage("Assignment is limited to selections visible on the current page.");
      return;
    }
    await applyAssignmentTo(selectedItems, input, { clearSelection: true });
  };

  const assignOne = (finding: FindingView, assigneeId: string | null) => {
    const dueStillOpen =
      finding.assignment?.dueAt && Date.parse(finding.assignment.dueAt) > Date.now()
        ? finding.assignment.dueAt
        : defaultAssignmentDueAt();
    void applyAssignmentTo(
      [finding],
      {
        assignment: assigneeId ? { assigneeId, dueAt: dueStillOpen } : null,
        rationale: assigneeId
          ? `Assigned to ${assigneeId} from the findings queue.`
          : "Cleared assignment from the findings queue.",
      },
      { pendingId: finding.id },
    );
  };

  const copyViewLink = useCallback(async () => {
    const href = new URL(`/findings${findingQueryToSearch(query)}`, window.location.origin).href;
    try {
      await navigator.clipboard.writeText(href);
      setShareMessage("Filtered view link copied.");
    } catch {
      setShareMessage("Copy was blocked. Bookmark the current browser URL instead.");
    }
  }, [query]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      const typing = isEditableKeyboardTarget(event.target);
      const overlay = resolveFindingQueueKey({
        key: event.key,
        ids: itemIds,
        current: storyId,
        open: Boolean(storyId),
        typing,
      });
      if (overlay) {
        event.preventDefault();
        openStory(overlay.id);
        return;
      }
      const action = resolveFindingListShortcut({
        key: event.key,
        shiftKey: event.shiftKey,
        ids: itemIds,
        focusedId,
        open: Boolean(storyId),
        typing,
      });
      if (!action) return;
      event.preventDefault();
      if (action.type === "focus") setFocusedId(action.id);
      if (action.type === "open") {
        openStory(action.id);
      }
      if (action.type === "close") {
        if (storyId) closeStory();
        else if (expanded.size > 0) setExpanded(new Set());
        else if (selected.size > 0) setSelected(new Set());
      }
      if (action.type === "select") toggleSelected(action.id);
      if (action.type === "next-page" && query.page < totalPages) {
        setQuery((current) => ({ ...current, page: current.page + 1 }));
      }
      if (action.type === "prev-page" && query.page > 1) {
        setQuery((current) => ({ ...current, page: current.page - 1 }));
      }
      if (action.type === "actionable") {
        setQuery((current) => ({
          ...current,
          statuses: [...ACTIONABLE_STATUSES],
          preset: "actionable",
          page: 1,
        }));
      }
      if (action.type === "all") {
        setQuery((current) => ({ ...current, statuses: [], preset: "all", page: 1 }));
      }
      if (action.type === "filters") setAdvancedOpen(true);
      if (action.type === "copy-link") void copyViewLink();
      if (action.type === "refresh") findings.refresh();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    closeStory,
    copyViewLink,
    expanded.size,
    findings,
    focusedId,
    itemIds,
    openStory,
    query.page,
    selected.size,
    setAdvancedOpen,
    storyId,
    toggleSelected,
    totalPages,
  ]);

  const saveCurrentView = async (name: string): Promise<boolean> => {
    setSavedViewPending(true);
    setSavedViewMessage("");
    try {
      await api.createSavedFindingView(name, findingQueryToSearch({ ...query, page: 1 }));
      setSavedViewMessage(`Saved “${name}”.`);
      savedViews.refresh();
      return true;
    } catch (error) {
      setSavedViewMessage(
        error instanceof ApiError ? error.message : "The current view could not be saved.",
      );
      return false;
    } finally {
      setSavedViewPending(false);
    }
  };

  const applySavedView = (view: SavedFindingView) => {
    setSelected(new Set());
    setExpanded(new Set());
    setQuery(findingQueryFromSearch(view.search));
    setSavedViewMessage(`Applied “${view.name}”.`);
  };

  const deleteSavedView = async (view: SavedFindingView) => {
    setSavedViewPending(true);
    setSavedViewMessage("");
    try {
      await api.deleteSavedFindingView(view.id);
      setSavedViewMessage(`Deleted “${view.name}”.`);
      savedViews.refresh();
    } catch (error) {
      setSavedViewMessage(
        error instanceof ApiError ? error.message : "The saved view could not be deleted.",
      );
    } finally {
      setSavedViewPending(false);
    }
  };

  const storyArtifactId = storyFinding
    ? modelAssist.draftedFixes.get(storyFinding.id)?.artifactId
    : undefined;

  return storyId ? (
    <section className="page findings-page findings-page--story">
      {storyFinding ? (
        <FindingInspector
          finding={storyFinding}
          modelExplanation={modelAssist.explanations.get(storyFinding.id)}
          modelDraftedFix={modelAssist.draftedFixes.get(storyFinding.id)}
          modelDraftedFixDecision={
            storyArtifactId ? (modelAssist.decisions.get(storyArtifactId) ?? null) : null
          }
          modelDecisionPending={
            storyArtifactId ? modelAssist.decisionPending.has(storyArtifactId) : false
          }
          modelProviders={modelAssist.providers}
          modelPending={modelAssist.pending.has(storyFinding.id)}
          modelMessage={modelAssist.messages.get(storyFinding.id) ?? ""}
          onExplain={(input) => modelAssist.onExplain(storyFinding.id, input)}
          onDraftFix={(input) => modelAssist.onDraftFix(storyFinding.id, input)}
          autoRemediation={modelAssist.remediations?.get(storyFinding.id)}
          autoRemediationPending={modelAssist.remediationPending?.has(storyFinding.id) ?? false}
          autoRemediationMessage={modelAssist.remediationMessages?.get(storyFinding.id) ?? ""}
          onAutoRemediate={
            modelAssist.onAutoRemediate
              ? () => {
                  modelAssist.onAutoRemediate?.(storyFinding.id);
                }
              : undefined
          }
          onDecideArtifact={(artifactId, decision) =>
            modelAssist.onDecideArtifact(storyFinding.id, artifactId, decision)
          }
          relatedFindings={items.filter((item) => item.snapshotId === storyFinding.snapshotId)}
          onOpenFinding={openStory}
          onClose={closeStory}
          onValidate={(input) => validate(storyFinding.id, input)}
          onSuppress={(suppressed, rationale) =>
            setSuppression(storyFinding.id, suppressed, rationale)
          }
          canRemediate={canRemediate}
          canReview={canReview}
        />
      ) : fetched.error ? (
        <div>
          <button type="button" onClick={closeStory}>
            Back to findings
          </button>
          <InlineError error={fetched.error} onRetry={fetched.refresh} />
        </div>
      ) : (
        <LoadingTable columns={2} rows={6} />
      )}
    </section>
  ) : (
    <section className="page findings-page" aria-label="Findings">
      <FindingFilters
        state={query}
        setState={setQuery}
        facets={facets.data}
        total={total}
        visibleStart={visibleStart}
        visibleEnd={visibleEnd}
        advancedOpen={advancedOpen}
        setAdvancedOpen={setAdvancedOpen}
        visibleColumns={visibleColumns}
        onToggleColumn={toggleColumn}
        onResetColumns={resetColumns}
        selectedCount={selected.size}
        exporting={exporting}
        onExportSelected={() => void performExport("selected")}
        onExportFiltered={() => void performExport("filtered")}
        onExportError={setExportError}
        onClearAll={() =>
          setQuery({ ...DEFAULT_FINDING_QUERY, statuses: [], preset: "all", page: 1 })
        }
        queueBreakdown={
          <FindingResultTiles
            total={total}
            summary={threatSummary}
            facets={facets.data}
            state={query}
            onApply={(patch) => {
              setSelected(new Set());
              setExpanded(new Set());
              setQuery((current) => ({ ...current, ...patch, page: 1 }));
            }}
          />
        }
        savedViews={
          <>
            <SavedViewsMenu
              views={savedViews.data?.items ?? []}
              loading={savedViews.isLoading && !savedViews.data}
              pending={savedViewPending}
              message={savedViewMessage}
              onSave={saveCurrentView}
              onApply={applySavedView}
              onDelete={deleteSavedView}
            />
            {savedViews.error && !savedViews.data ? (
              <InlineError error={savedViews.error} onRetry={savedViews.refresh} />
            ) : null}
          </>
        }
        onCopyLink={() => void copyViewLink()}
        onRefresh={findings.refresh}
        refreshing={findings.isRefreshing}
        shareMessage={shareMessage}
      />

      {canReview && selected.size > 0 ? (
        <BulkAssignmentControl
          selected={selectedItems}
          assignees={assignmentAssignees}
          pending={assignmentPending}
          message={assignmentMessage}
          onApply={applyAssignment}
        />
      ) : null}

      {exportError ? (
        <InlineError error={exportError} onDismiss={() => setExportError(null)} />
      ) : null}
      {facets.error && facets.data ? (
        <InlineError error={facets.error} onRetry={facets.refresh} />
      ) : null}
      {findings.error && findings.data ? (
        <InlineError error={findings.error} onRetry={findings.refresh} />
      ) : null}

      {findings.isLoading && !findings.data ? <LoadingTable columns={9} rows={10} /> : null}
      {findings.error && !findings.data ? (
        <InlineError error={findings.error} onRetry={findings.refresh} />
      ) : null}
      {!findings.isLoading && findings.data && items.length === 0 ? (
        <EmptyState
          title={query.preset === "actionable" ? "No actionable findings" : "No findings match"}
          description={
            query.preset === "actionable"
              ? "No confirmed or needs-human rows are in this filter. That is not a clean scan — open Evidence to see what was actually reviewed."
              : "No rows match these filters. That is not a clean scan — open Evidence before treating the estate as clear."
          }
          action={
            <div className="empty-state__actions">
              <a
                className="button"
                href={coverageGapsHref(query.scanId)}
                onClick={
                  navigate ? (event) => handleInternalNavigation(event, navigate) : undefined
                }
              >
                Open evidence gaps
              </a>
              <button
                className="button"
                type="button"
                onClick={() => setQuery({ ...DEFAULT_FINDING_QUERY, statuses: [], preset: "all" })}
              >
                View all findings
              </button>
            </div>
          }
        />
      ) : null}
      {items.length > 0 ? (
        <FindingsTable
          findings={items}
          queryState={query}
          visibleColumns={visibleColumns}
          selected={selected}
          focusedId={focusedId ?? undefined}
          onSort={sort}
          onToggleSelected={toggleSelected}
          onTogglePageSelected={togglePageSelected}
          onOpenStory={openStory}
          assignees={canReview ? assignmentAssignees : []}
          assignmentPendingId={assignmentPendingId}
          onAssign={canReview ? assignOne : undefined}
        />
      ) : null}

      {total > 0 ? (
        <nav className="pagination" aria-label="Finding pages">
          <label>
            Rows
            <select
              value={query.pageSize}
              onChange={(event) =>
                setQuery((current) => ({
                  ...current,
                  pageSize: Number(event.target.value),
                  page: 1,
                }))
              }
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
          <span>
            Page {formatInteger(query.page)} of {formatInteger(totalPages)}
          </span>
          <button
            className="icon-button"
            type="button"
            aria-label="Previous page"
            disabled={query.page <= 1}
            onClick={() => setQuery((current) => ({ ...current, page: current.page - 1 }))}
          >
            <ChevronLeft aria-hidden="true" size={17} />
          </button>
          <button
            className="icon-button"
            type="button"
            aria-label="Next page"
            disabled={query.page >= totalPages}
            onClick={() => setQuery((current) => ({ ...current, page: current.page + 1 }))}
          >
            <ChevronRight aria-hidden="true" size={17} />
          </button>
        </nav>
      ) : null}
    </section>
  );
}
